import React, { useEffect, useMemo, useState } from "react";

const FUND_HOLDINGS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/smart_money_fund_holdings.csv";
const RAW_TRANSACTIONS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/smart_money_raw.csv";
const UNMAPPED_CUSIPS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/smart_money_unmapped_cusips.csv";

const TABS = [
  { key: "changes", label: "Latest Quarter Changes" },
  { key: "holdings", label: "Latest Holdings" },
  { key: "raw", label: "Mapped Transactions" },
  { key: "unmapped", label: "Unmapped CUSIPs" },
];

const CHANGE_COLUMNS = [
  { key: "fundName", label: "Fund", width: 190 },
  { key: "issuer", label: "Issuer", width: 240 },
  { key: "ticker", label: "Ticker", width: 82 },
  { key: "cusip", label: "CUSIP", width: 105 },
  { key: "changeType", label: "Change", type: "changeType", align: "center", width: 110 },
  { key: "currentValueUsd", label: "Current Value", type: "moneyLarge", align: "right", width: 125 },
  { key: "priorValueUsd", label: "Prior Value", type: "moneyLarge", align: "right", width: 120 },
  { key: "valueChangeUsd", label: "Value Change", type: "moneyLargeSigned", align: "right", width: 125 },
  { key: "currentShares", label: "Current Shares", type: "number", align: "right", width: 120 },
  { key: "priorShares", label: "Prior Shares", type: "number", align: "right", width: 110 },
  { key: "sharesChange", label: "Shares Change", type: "numberSigned", align: "right", width: 120 },
  { key: "sharesChangePct", label: "Shares Change %", type: "percentAlreadyDecimalSigned", align: "right", width: 130 },
  { key: "currentReportDate", label: "Current Qtr", width: 105 },
  { key: "priorReportDate", label: "Prior Qtr", width: 105 },
];

const HOLDINGS_COLUMNS = [
  { key: "fundName", label: "Fund", width: 190 },
  { key: "issuer", label: "Issuer", width: 230 },
  { key: "ticker", label: "Ticker", width: 82 },
  { key: "cusip", label: "CUSIP", width: 105 },
  { key: "valueUsd", label: "Value", type: "moneyLarge", align: "right", width: 120 },
  { key: "shares", label: "Shares", type: "number", align: "right", width: 110 },
  { key: "reportDate", label: "Report Date", width: 105 },
  { key: "filingDate", label: "Filing Date", width: 105 },
];

const RAW_COLUMNS = [
  { key: "ticker", label: "Ticker", width: 82 },
  { key: "issuer", label: "Issuer", width: 230 },
  { key: "fundName", label: "Fund", width: 190 },
  { key: "cusip", label: "CUSIP", width: 105 },
  { key: "latestValueUsd", label: "Latest Value", type: "moneyLarge", align: "right", width: 125 },
  { key: "priorValueUsd", label: "Prior Value", type: "moneyLarge", align: "right", width: 120 },
  { key: "valueAddedUsd", label: "Value Added", type: "moneyLargeSigned", align: "right", width: 125 },
  { key: "latestShares", label: "Latest Shares", type: "number", align: "right", width: 120 },
  { key: "priorShares", label: "Prior Shares", type: "number", align: "right", width: 110 },
  { key: "sharesAdded", label: "Shares Added", type: "numberSigned", align: "right", width: 120 },
  { key: "sharesAddedPct", label: "Shares Added %", type: "percentAlreadyDecimalSigned", align: "right", width: 130 },
  { key: "positionWeightPct", label: "Position Weight", type: "percentAlreadyDecimal", align: "right", width: 130 },
  { key: "isNewPosition", label: "New?", type: "boolean", align: "center", width: 75 },
  { key: "isBuying", label: "Buying?", type: "boolean", align: "center", width: 80 },
  { key: "isSelling", label: "Selling?", type: "boolean", align: "center", width: 80 },
  { key: "latestReportDate", label: "Report Date", width: 105 },
  { key: "latestFilingDate", label: "Filing Date", width: 105 },
];

const UNMAPPED_COLUMNS = [
  { key: "issuer", label: "Issuer", width: 280 },
  { key: "cusip", label: "CUSIP", width: 110 },
  { key: "totalValueUsd", label: "Total Value", type: "moneyLarge", align: "right", width: 130 },
  { key: "fundCount", label: "Fund Count", type: "integer", align: "right", width: 105 },
  { key: "exampleFunds", label: "Example Funds", width: 420 },
];

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < String(line || "").length; i++) {
    const ch = String(line || "")[i];
    const next = String(line || "")[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function parseCsv(text) {
  const clean = String(text || "").trim();
  if (!clean) return [];

  const lines = clean.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((h) => String(h || "").trim());

  return lines.slice(1).map((line, index) => {
    const cols = parseCsvLine(line);
    const row = { __rowId: index };

    headers.forEach((header, i) => {
      row[header] = cols[i] ?? "";
    });

    return row;
  });
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function cleanTicker(value) {
  return String(value || "").trim().toUpperCase();
}

function cleanCusip(value) {
  return String(value || "").trim().toUpperCase();
}

function fundKey(row) {
  return `${String(row.fundName || "").trim()}|${String(row.fundCik || "").trim()}`;
}

function securityKey(row) {
  return cleanCusip(row.cusip) || `${cleanTicker(row.ticker)}|${String(row.issuer || "").trim().toUpperCase()}`;
}

function dateValue(value) {
  const time = new Date(String(value || "")).getTime();
  return Number.isFinite(time) ? time : 0;
}

function formatNumber(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatSignedNumber(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatInteger(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatMoneyLarge(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);
  if (abs >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}

function formatMoneyLargeSigned(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  const formatted = formatMoneyLarge(Math.abs(n));
  return `${sign}${formatted}`;
}

function formatPercentAlreadyDecimal(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function formatPercentAlreadyDecimalSigned(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(1)}%`;
}

function formatBoolean(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "—";
  if (raw === "true" || raw === "1" || raw === "yes") return "Yes";
  if (raw === "false" || raw === "0" || raw === "no") return "No";
  return String(value);
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "moneyLarge") return formatMoneyLarge(value);
  if (type === "moneyLargeSigned") return formatMoneyLargeSigned(value);
  if (type === "number") return formatNumber(value);
  if (type === "numberSigned") return formatSignedNumber(value);
  if (type === "integer") return formatInteger(value);
  if (type === "percentAlreadyDecimal") return formatPercentAlreadyDecimal(value);
  if (type === "percentAlreadyDecimalSigned") return formatPercentAlreadyDecimalSigned(value);
  if (type === "boolean") return formatBoolean(value);
  return String(value);
}

function getSortValue(row, key) {
  const value = row?.[key];

  if (
    key.toLowerCase().includes("value") ||
    key.toLowerCase().includes("shares") ||
    key.toLowerCase().includes("count") ||
    key.toLowerCase().includes("pct") ||
    key.toLowerCase().includes("weight")
  ) {
    const n = parseNumber(value);
    return Number.isFinite(n) ? n : null;
  }

  if (key.toLowerCase().includes("date")) {
    return dateValue(value);
  }

  return value;
}

function uniqueSorted(rows, key) {
  return Array.from(
    new Set(rows.map((row) => String(row?.[key] || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
}

function getLatestAndPriorReportDatesByFund(rows) {
  const reportDatesByFund = new Map();

  rows.forEach((row) => {
    const key = fundKey(row);
    const reportDate = String(row.reportDate || "").trim();
    if (!key || !reportDate) return;

    if (!reportDatesByFund.has(key)) reportDatesByFund.set(key, new Set());
    reportDatesByFund.get(key).add(reportDate);
  });

  const out = new Map();

  for (const [key, dateSet] of reportDatesByFund.entries()) {
    const dates = Array.from(dateSet).sort((a, b) => dateValue(b) - dateValue(a));
    out.set(key, {
      latest: dates[0] || "",
      prior: dates[1] || "",
    });
  }

  return out;
}

function getLatestHoldings(rows) {
  const datesByFund = getLatestAndPriorReportDatesByFund(rows);

  return rows.filter((row) => {
    const dates = datesByFund.get(fundKey(row));
    return dates?.latest && String(row.reportDate || "").trim() === dates.latest;
  });
}

function getLatestQuarterChangeRows(rows) {
  const datesByFund = getLatestAndPriorReportDatesByFund(rows);
  const rowsByFund = new Map();

  rows.forEach((row) => {
    const key = fundKey(row);
    if (!rowsByFund.has(key)) rowsByFund.set(key, []);
    rowsByFund.get(key).push(row);
  });

  const changes = [];

  for (const [key, fundRows] of rowsByFund.entries()) {
    const dates = datesByFund.get(key);
    if (!dates?.latest) continue;

    const latestRows = fundRows.filter((row) => String(row.reportDate || "").trim() === dates.latest);
    const priorRows = dates.prior
      ? fundRows.filter((row) => String(row.reportDate || "").trim() === dates.prior)
      : [];

    const latestBySecurity = new Map();
    const priorBySecurity = new Map();

    latestRows.forEach((row) => latestBySecurity.set(securityKey(row), row));
    priorRows.forEach((row) => priorBySecurity.set(securityKey(row), row));

    const allSecurityKeys = new Set([
      ...Array.from(latestBySecurity.keys()),
      ...Array.from(priorBySecurity.keys()),
    ]);

    for (const secKey of allSecurityKeys) {
      const current = latestBySecurity.get(secKey);
      const prior = priorBySecurity.get(secKey);

      const base = current || prior;
      if (!base) continue;

      const currentShares = parseNumber(current?.shares) || 0;
      const priorShares = parseNumber(prior?.shares) || 0;
      const currentValueUsd = parseNumber(current?.valueUsd) || 0;
      const priorValueUsd = parseNumber(prior?.valueUsd) || 0;

      const sharesChange = currentShares - priorShares;
      const valueChangeUsd = currentValueUsd - priorValueUsd;
      const sharesChangePct = priorShares > 0 ? sharesChange / priorShares : null;

      let changeType = "Unchanged";
      if (current && !prior) changeType = "New";
      else if (!current && prior) changeType = "Sold Out";
      else if (sharesChange > 0) changeType = "Added";
      else if (sharesChange < 0) changeType = "Reduced";

      changes.push({
        __rowId: `${key}-${secKey}`,
        fundName: base.fundName || "",
        fundCik: base.fundCik || "",
        issuer: base.issuer || "",
        ticker: current?.ticker || prior?.ticker || "",
        cusip: current?.cusip || prior?.cusip || "",
        changeType,
        currentValueUsd,
        priorValueUsd,
        valueChangeUsd,
        currentShares,
        priorShares,
        sharesChange,
        sharesChangePct,
        currentReportDate: dates.latest,
        priorReportDate: dates.prior || "—",
        currentFilingDate: current?.filingDate || "",
        priorFilingDate: prior?.filingDate || "",
      });
    }
  }

  return changes;
}

function summarizeLatestHoldings(rows) {
  const totalValue = rows.reduce((sum, row) => sum + (parseNumber(row.valueUsd) || 0), 0);
  const mapped = rows.filter((row) => String(row.ticker || "").trim()).length;
  const funds = uniqueSorted(rows, "fundName").length;
  const issuers = uniqueSorted(rows, "issuer").length;
  return { totalValue, mapped, funds, issuers };
}

function summarizeChanges(rows) {
  return {
    total: rows.length,
    newPositions: rows.filter((row) => row.changeType === "New").length,
    added: rows.filter((row) => row.changeType === "Added").length,
    reduced: rows.filter((row) => row.changeType === "Reduced").length,
    soldOut: rows.filter((row) => row.changeType === "Sold Out").length,
    valueChange: rows.reduce((sum, row) => sum + (parseNumber(row.valueChangeUsd) || 0), 0),
  };
}

// function summarizeRaw(rows) {
//   const valueAdded = rows.reduce((sum, row) => sum + (parseNumber(row.valueAddedUsd) || 0), 0);
//   const buying = rows.filter((row) => formatBoolean(row.isBuying) === "Yes").length;
//   const selling = rows.filter((row) => formatBoolean(row.isSelling) === "Yes").length;
//   const newPositions = rows.filter((row) => formatBoolean(row.isNewPosition) === "Yes").length;
//   return { valueAdded, buying, selling, newPositions };
// }

function summarizeUnmapped(rows) {
  const totalValue = rows.reduce((sum, row) => sum + (parseNumber(row.totalValueUsd) || 0), 0);
  const issuers = uniqueSorted(rows, "issuer").length;
  const cusips = uniqueSorted(rows, "cusip").length;
  return { totalValue, issuers, cusips };
}

export default function SmartMoneyRawHoldingsPage() {
  const [activeTab, setActiveTab] = useState("changes");
  const [allFundHoldings, setAllFundHoldings] = useState([]);
  const [rawTransactions, setRawTransactions] = useState([]);
  const [unmappedCusips, setUnmappedCusips] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [fundFilter, setFundFilter] = useState("");
  const [tickerFilter, setTickerFilter] = useState("");
  const [changeTypeFilter, setChangeTypeFilter] = useState("");
  const [sortKey, setSortKey] = useState("valueChangeUsd");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    async function fetchCsv(url) {
      const res = await fetch(url, { cache: "no-store" });
      const text = await res.text();

      if (!res.ok) throw new Error(`Fetch failed ${res.status}: ${url}`);
      if (text.trim().startsWith("<")) throw new Error(`Expected CSV but received HTML: ${url}`);

      return parseCsv(text);
    }

    async function loadAll() {
      try {
        setLoading(true);
        setError("");

        const [holdingsRows, rawRows, unmappedRows] = await Promise.all([
          fetchCsv(FUND_HOLDINGS_URL),
          fetchCsv(RAW_TRANSACTIONS_URL).catch(() => []),
          fetchCsv(UNMAPPED_CUSIPS_URL),
        ]);

        setAllFundHoldings(holdingsRows);
        setRawTransactions(rawRows);
        setUnmappedCusips(unmappedRows);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load smart money files.");
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, []);

  const latestHoldings = useMemo(() => getLatestHoldings(allFundHoldings), [allFundHoldings]);

  const latestQuarterChanges = useMemo(
    () => getLatestQuarterChangeRows(allFundHoldings),
    [allFundHoldings]
  );

  const currentRows = useMemo(() => {
    if (activeTab === "changes") return latestQuarterChanges;
    if (activeTab === "holdings") return latestHoldings;
    if (activeTab === "raw") return rawTransactions;
    if (activeTab === "unmapped") return unmappedCusips;
    return [];
  }, [activeTab, latestQuarterChanges, latestHoldings, rawTransactions, unmappedCusips]);

  const currentColumns = useMemo(() => {
    if (activeTab === "changes") return CHANGE_COLUMNS;
    if (activeTab === "holdings") return HOLDINGS_COLUMNS;
    if (activeTab === "raw") return RAW_COLUMNS;
    if (activeTab === "unmapped") return UNMAPPED_COLUMNS;
    return [];
  }, [activeTab]);

  const fundOptions = useMemo(() => {
    if (activeTab === "unmapped") return [];
    return uniqueSorted(currentRows, "fundName");
  }, [activeTab, currentRows]);

  const tickerOptions = useMemo(() => {
    if (activeTab === "unmapped") return [];
    return uniqueSorted(currentRows, "ticker");
  }, [activeTab, currentRows]);

  const changeTypeOptions = useMemo(() => {
    if (activeTab !== "changes") return [];
    return uniqueSorted(currentRows, "changeType");
  }, [activeTab, currentRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const data = currentRows.filter((row) => {
      const matchesSearch =
        !q || Object.values(row).some((value) => String(value || "").toLowerCase().includes(q));

      const matchesFund = !fundFilter || String(row.fundName || "").trim() === fundFilter;
      const matchesTicker = !tickerFilter || String(row.ticker || "").trim().toUpperCase() === tickerFilter;
      const matchesChangeType =
        !changeTypeFilter || String(row.changeType || "").trim() === changeTypeFilter;

      return matchesSearch && matchesFund && matchesTicker && matchesChangeType;
    });

    return [...data].sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);

      if (av === null || av === undefined || av === "") return 1;
      if (bv === null || bv === undefined || bv === "") return -1;

      const an = Number(av);
      const bn = Number(bv);

      if (Number.isFinite(an) && Number.isFinite(bn)) return sortDir === "asc" ? an - bn : bn - an;

      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [
    currentRows,
    search,
    fundFilter,
    tickerFilter,
    changeTypeFilter,
    sortKey,
    sortDir,
  ]);

  const holdingSummary = useMemo(() => summarizeLatestHoldings(latestHoldings), [latestHoldings]);
  const changeSummary = useMemo(() => summarizeChanges(latestQuarterChanges), [latestQuarterChanges]);
  //const rawSummary = useMemo(() => summarizeRaw(rawTransactions), [rawTransactions]);
  const unmappedSummary = useMemo(() => summarizeUnmapped(unmappedCusips), [unmappedCusips]);

  function handleTabChange(tabKey) {
    setActiveTab(tabKey);
    setSearch("");
    setFundFilter("");
    setTickerFilter("");
    setChangeTypeFilter("");

    if (tabKey === "changes") {
      setSortKey("valueChangeUsd");
      setSortDir("desc");
    } else if (tabKey === "raw") {
      setSortKey("valueAddedUsd");
      setSortDir("desc");
    } else if (tabKey === "unmapped") {
      setSortKey("totalValueUsd");
      setSortDir("desc");
    } else {
      setSortKey("valueUsd");
      setSortDir("desc");
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div>
          <div style={eyebrow}>Majorah Smart Money</div>
          <h1 style={title}>Raw 13F Holdings Explorer</h1>
          <p style={subtitle}>
            View each fund’s latest available quarter only, compare against the
            prior quarter, and include full sell-outs alongside adds, reductions,
            and new positions.
          </p>
        </div>
      </section>

      <section style={summaryGrid}>
        <SummaryCard
          label="Latest Holdings"
          value={latestHoldings.length.toLocaleString()}
          detail={`${holdingSummary.funds.toLocaleString()} funds · ${holdingSummary.issuers.toLocaleString()} issuers`}
        />
        <SummaryCard
          label="New / Added"
          value={`${changeSummary.newPositions.toLocaleString()} / ${changeSummary.added.toLocaleString()}`}
          detail={`New positions / added positions`}
        />
        <SummaryCard
          label="Reduced / Sold Out"
          value={`${changeSummary.reduced.toLocaleString()} / ${changeSummary.soldOut.toLocaleString()}`}
          detail={`Reduced positions / full exits`}
        />
        <SummaryCard
          label="Unmapped CUSIPs"
          value={unmappedCusips.length.toLocaleString()}
          detail={`${unmappedSummary.issuers.toLocaleString()} issuers · ${formatMoneyLarge(unmappedSummary.totalValue)}`}
        />
      </section>

      <section style={controlCard}>
        <div style={tabRow}>
          {TABS.map((tab) => (
            <button key={tab.key} type="button" onClick={() => handleTabChange(tab.key)} style={tabButton(activeTab === tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={filtersRow}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fund, issuer, ticker, CUSIP..." style={searchInput} />

          {activeTab !== "unmapped" ? (
            <>
              <select value={fundFilter} onChange={(e) => setFundFilter(e.target.value)} style={selectInput}>
                <option value="">All funds</option>
                {fundOptions.map((fund) => (
                  <option key={fund} value={fund}>
                    {fund}
                  </option>
                ))}
              </select>

              <select value={tickerFilter} onChange={(e) => setTickerFilter(e.target.value)} style={selectInput}>
                <option value="">All tickers</option>
                {tickerOptions.map((ticker) => (
                  <option key={ticker} value={ticker}>
                    {ticker || "No ticker"}
                  </option>
                ))}
              </select>
            </>
          ) : null}

          {activeTab === "changes" ? (
            <select value={changeTypeFilter} onChange={(e) => setChangeTypeFilter(e.target.value)} style={selectInput}>
              <option value="">All changes</option>
              {changeTypeOptions.map((changeType) => (
                <option key={changeType} value={changeType}>
                  {changeType}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </section>

      {loading ? (
        <div style={message}>Loading smart money files...</div>
      ) : error ? (
        <div style={errorBox}>{error}</div>
      ) : (
        <section style={tableCard}>
          <div style={tableHeader}>
            <div>
              <h2 style={sectionTitle}>{TABS.find((tab) => tab.key === activeTab)?.label}</h2>
              <p style={sectionSubtitle}>
                Showing {filteredRows.length.toLocaleString()} of {currentRows.length.toLocaleString()} rows
              </p>
            </div>
          </div>

          {activeTab === "raw" && rawTransactions.length === 0 ? (
            <div style={emptyState}>
              <strong>No mapped transactions yet.</strong>
              <span>
                SEC holdings were parsed, but CUSIPs have not been resolved into
                tickers yet. The Latest Quarter Changes tab still works from raw
                CUSIP-level holdings.
              </span>
            </div>
          ) : null}

          <div style={tableWrap}>
            <table style={{ ...table, minWidth: currentColumns.reduce((sum, col) => sum + col.width, 0) }}>
              <thead>
                <tr>
                  {currentColumns.map((col) => (
                    <th key={col.key} onClick={() => handleSort(col.key)} style={getHeaderStyle(col)}>
                      {col.label}
                      {sortKey === col.key ? <span style={sortIndicator}>{sortDir === "asc" ? " ▲" : " ▼"}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row, index) => (
                  <tr key={`${activeTab}-${row.__rowId}-${index}`} style={index % 2 === 0 ? rowEven : rowOdd}>
                    {currentColumns.map((col) => (
                      <td key={col.key} style={getCellStyle(col)}>
                        {renderCell(row, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={noteBox}>
            <strong>Note:</strong> “Sold Out” rows are securities that appeared in
            the prior quarter for that fund but no longer appear in its latest
            quarter. This table compares each fund’s latest available report date
            against that same fund’s immediately prior report date.
          </div>
        </section>
      )}
    </div>
  );
}

function SummaryCard({ label, value, detail }) {
  return (
    <div style={summaryCard}>
      <div style={summaryLabel}>{label}</div>
      <div style={summaryValue}>{value}</div>
      <div style={summaryDetail}>{detail}</div>
    </div>
  );
}

function renderCell(row, col) {
  const value = row[col.key];

  if (col.key === "ticker") {
    const ticker = String(value || "").trim().toUpperCase();
    if (!ticker) return <span style={mutedText}>Unmapped</span>;
    return <span style={tickerPill}>{ticker}</span>;
  }

  if (col.key === "changeType") {
    return <span style={changePill(value)}>{value || "—"}</span>;
  }

  if (col.key === "isNewPosition" || col.key === "isBuying" || col.key === "isSelling") {
    const formatted = formatBoolean(value);
    return <span style={booleanPill(formatted)}>{formatted}</span>;
  }

  if (
    col.type === "moneyLargeSigned" ||
    col.type === "numberSigned" ||
    col.type === "percentAlreadyDecimalSigned"
  ) {
    return <span style={signedValueStyle(value)}>{formatValue(value, col.type)}</span>;
  }

  return formatValue(value, col.type);
}

function getHeaderStyle(col) {
  return {
    ...thBase,
    textAlign: col.align || "left",
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
  };
}

function getCellStyle(col) {
  return {
    ...tdBase,
    textAlign: col.align || "left",
    minWidth: col.width,
    width: col.width,
    maxWidth: col.width,
  };
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 22,
  background:
    "radial-gradient(circle at 16% 12%, rgba(56, 189, 248, 0.23), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 24,
  boxShadow: "0 22px 52px rgba(15, 23, 42, 0.2)",
};

const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 32,
  letterSpacing: "-0.05em",
  lineHeight: 1.02,
};

const subtitle = {
  maxWidth: 980,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.65,
  fontSize: 14,
};

const summaryGrid = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: 12,
};

const summaryCard = {
  padding: 16,
  borderRadius: 18,
  background: "white",
  border: "1px solid #e2e8f0",
  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.07)",
};

const summaryLabel = {
  fontSize: 10,
  color: "#64748b",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const summaryValue = {
  marginTop: 8,
  fontSize: 24,
  fontWeight: 950,
  letterSpacing: "-0.05em",
};

const summaryDetail = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 12,
};

const controlCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255,255,255,0.92)",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
  display: "grid",
  gap: 14,
};

const tabRow = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

function tabButton(active) {
  return {
    padding: "9px 12px",
    borderRadius: 999,
    border: active ? "1px solid #93c5fd" : "1px solid #e2e8f0",
    background: active ? "#eff6ff" : "white",
    color: active ? "#1d4ed8" : "#475569",
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
  };
}

const filtersRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const searchInput = {
  minWidth: 260,
  flex: "1 1 320px",
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

const selectInput = {
  minWidth: 190,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  fontSize: 12,
  background: "white",
  outline: "none",
};

const tableCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const tableHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const sectionTitle = {
  margin: 0,
  fontSize: 17,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  maxHeight: "76vh",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const thBase = {
  padding: "9px 9px",
  fontSize: 10,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  zIndex: 6,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const tdBase = {
  padding: "8px 9px",
  fontSize: 12,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontVariantNumeric: "tabular-nums",
};

const rowEven = { background: "#ffffff" };
const rowOdd = { background: "#fbfdff" };

const sortIndicator = {
  color: "#0f172a",
  fontSize: 10,
};

const tickerPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 52,
  padding: "4px 7px",
  borderRadius: 999,
  color: "#1d4ed8",
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  fontWeight: 950,
  fontSize: 11,
};

const mutedText = {
  color: "#94a3b8",
  fontSize: 12,
};

function changePill(value) {
  const v = String(value || "");
  const map = {
    New: { color: "#047857", background: "#ecfdf5", border: "#a7f3d0" },
    Added: { color: "#15803d", background: "#f0fdf4", border: "#bbf7d0" },
    Reduced: { color: "#b45309", background: "#fffbeb", border: "#fde68a" },
    "Sold Out": { color: "#be123c", background: "#fff1f2", border: "#fecdd3" },
    Unchanged: { color: "#64748b", background: "#f8fafc", border: "#e2e8f0" },
  };

  const style = map[v] || map.Unchanged;

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 72,
    padding: "4px 7px",
    borderRadius: 999,
    color: style.color,
    background: style.background,
    border: `1px solid ${style.border}`,
    fontWeight: 900,
    fontSize: 11,
  };
}

function signedValueStyle(value) {
  const n = parseNumber(value);
  return {
    color: n > 0 ? "#047857" : n < 0 ? "#be123c" : "#475569",
    fontWeight: n !== 0 ? 850 : 500,
  };
}

function booleanPill(value) {
  const yes = value === "Yes";
  const no = value === "No";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 48,
    padding: "4px 7px",
    borderRadius: 999,
    color: yes ? "#047857" : no ? "#64748b" : "#94a3b8",
    background: yes ? "#ecfdf5" : no ? "#f8fafc" : "#f8fafc",
    border: yes ? "1px solid #a7f3d0" : "1px solid #e2e8f0",
    fontWeight: 850,
    fontSize: 11,
  };
}

const emptyState = {
  display: "grid",
  gap: 6,
  marginBottom: 14,
  padding: 14,
  borderRadius: 14,
  border: "1px solid #fde68a",
  background: "#fffbeb",
  color: "#92400e",
  fontSize: 13,
  lineHeight: 1.55,
};

const noteBox = {
  marginTop: 12,
  padding: 12,
  borderRadius: 14,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
  fontSize: 12,
  lineHeight: 1.55,
};

const message = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};
